import styled from 'styled-components';
import type {
  BannerMediaPlacement,
  ButtonVariation,
  DoubleBannerDescriptions,
  MediaAspectRatio,
  MediaFit,
} from '@amplience/content-types/typings/c-double-banner';
import { useMediaMatch } from '@storefront/lib-global/hooks/useMediaMatch';
import { FlexibleTextPartial } from '@storefront/lib-global/partials/flexibleText';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import type { MediaPartialProps } from '@storefront/lib-global/partials/media';
import { MediaPartial } from '@storefront/lib-global/partials/media';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import { TypographyStyles, TypographyStylesType, colours, media, spacing } from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { BannerButtonStyles, twoColumnTextAlignment } from '../../utils';
import type { DoubleBannerProps } from './bannerType';

export type TwoColumnBannerProps = DoubleBannerProps;

const S = {
  TwoColumnBannerContainer: styled.div<{
    $aspectRatio?: MediaAspectRatio;
    $bannerMediaPosition?: BannerMediaPlacement;
  }>`
    display: flex;
    flex-direction: column;
    gap: ${spacing.S};
    padding: ${spacing.S} 0;

    @media ${media.greaterThan('lg')} {
      flex-direction: ${({ $bannerMediaPosition }) => ($bannerMediaPosition === 'Image Left' ? 'row' : 'row-reverse')};
      gap: ${({ $aspectRatio }) => ($aspectRatio === '4/3' ? '80px' : '48px')};
    }

    @media ${media.greaterThan('xl')} {
      padding: 70px 0;
    }
  `,
  TwoColumnBannerMediasContainer: styled.div<{
    $isPlacedBelowText: boolean;
    $aspectRatio?: MediaAspectRatio;
    $textWidth?: string;
  }>`
    height: 100%;
    order: ${({ $isPlacedBelowText }) => $isPlacedBelowText && 1};
    width: 100%;

    @media ${media.greaterThan('lg')} {
      display: flex;
      flex: ${({ $textWidth }) => `1 1 calc(100% - ${$textWidth || '50%'})`};
      flex-direction: column;
      gap: ${spacing.S};
      order: unset;
      padding-left: 6px;
    }
  `,
  TwoColumnBannerMediaContainer: styled.div<{
    $aspectRatio?: MediaAspectRatio;
    $mediaFit?: MediaFit;
  }>`
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || '4/3'};
    position: relative;

    & video {
      height: 100%;
      object-fit: ${({ $mediaFit }) => $mediaFit};
      position: absolute;
      width: 100%;
    }

    @media ${media.greaterThan('lg')} {
      margin: unset;
    }
  `,
  TwoColumnBannerDetails: styled.div<{ $aspectRatio?: MediaAspectRatio; $textWidth?: string; $textAlign?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${({ $aspectRatio }) => ($aspectRatio === '4/3' ? spacing.XS : spacing.XXS)};
    overflow-wrap: anywhere;
    width: 100%;

    @media ${media.greaterThan('lg')} {
      gap: ${({ $aspectRatio }) => ($aspectRatio === '4/3' ? '44px' : spacing.XS)};
      justify-content: ${({ $textAlign = 'center' }) => $textAlign};
      margin: unset;
      padding-left: 4px;
      width: ${({ $textWidth }) => $textWidth || '50%'};
    }
  `,
  TwoColumnBannerTitle: styled(FlexibleTextPartial)<{ $textSize?: string }>`
    margin: 0;
    overflow-wrap: anywhere;

    @media ${media.greaterThan('lg')} {
      margin: unset;
    }
  `,
  TwoColumnBannerDescription: styled(RichTextPartial)<{ $textColor?: string }>`
    color: ${({ $textColor }) => $textColor};
    overflow-wrap: break-word;
    width: 100%;

    & > p {
      margin: 0;
    }

    & a {
      color: ${({ $textColor }) => $textColor};
    }

    @media ${media.greaterThan('lg')} {
      margin: 0;
    }
  `,
  TwoColumnBannerButtonsContainer: styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${spacing.XS};
    margin: 0;
    padding: 0;

    & li,
    & a {
      max-width: 100%;
      width: 100%;
    }

    @media ${media.greaterThan('lg')} {
      & a {
        max-width: 340px;
      }
    }
  `,
  TwoColumnBannerButton: styled(LinkPartial)<{ $variation?: ButtonVariation }>`
    ${({ $variation }) => BannerButtonStyles($variation)}
    max-width: 340px;
  `,
  MobileContainer: styled.div`
    @media ${media.greaterThan('lg')} {
      display: none;
    }
  `,
  DesktopContainer: styled.div<{ $show?: boolean }>`
    display: ${({ $show }) => ($show ? 'flex' : 'none')};

    @media ${media.greaterThan('lg')} {
      display: flex;
      flex-direction: column;
    }
  `,
};

export function TwoColumnBanner({
  bannerTitle,
  bannerDescriptions,
  bannerMedia,
  bannerMediaPosition,
  bannerButtons,
  textWidth,
  textAlignment,
  onClick,
}: TwoColumnBannerProps) {
  const isDesktop = useMediaMatch(media.greaterThan('lg'));
  const currentMedia = isDesktop ? bannerMedia?.desktopMedia : bannerMedia?.mobileMedia;
  const aspectRatio = bannerMedia?.aspectRatio;
  const [firstMedia, secondMedia] = currentMedia ?? [];

  return (
    <S.TwoColumnBannerContainer $aspectRatio={aspectRatio} $bannerMediaPosition={bannerMediaPosition}>
      {!!bannerTitle?.text && bannerTitle?.textSize !== 'Headings - H5' && (
        <S.MobileContainer>
          <S.TwoColumnBannerTitle
            {...bannerTitle}
            text={getLocalizedValue(bannerTitle?.text)}
            defaultTextColor={colours.BLACK}
            defaultTextSize={TypographyStylesType.HEADINGS_X_LARGE}
          />
        </S.MobileContainer>
      )}
      <S.TwoColumnBannerMediasContainer
        $isPlacedBelowText={!isDesktop && !secondMedia && bannerMedia?.position === 'BOTTOM'}
        $aspectRatio={aspectRatio}
        $textWidth={textWidth}
      >
        <S.TwoColumnBannerMediaContainer $aspectRatio={aspectRatio} $mediaFit={firstMedia?.mediaFit}>
          <MediaPartial
            {...(firstMedia as MediaPartialProps)}
            height={100}
            width={100}
            isImageLayoutFill
            mediaFit={firstMedia?.mediaFit}
            videoQuality={firstMedia?.videoQuality}
            altText={firstMedia?.altText}
            key={firstMedia?.media?.name}
          />
        </S.TwoColumnBannerMediaContainer>
        {secondMedia && (
          <S.DesktopContainer>
            <S.TwoColumnBannerMediaContainer $aspectRatio={aspectRatio} $mediaFit={secondMedia.mediaFit}>
              <MediaPartial
                {...(secondMedia as MediaPartialProps)}
                height={100}
                width={100}
                isImageLayoutFill
                mediaFit={secondMedia?.mediaFit}
                videoQuality={secondMedia?.videoQuality}
                altText={secondMedia?.altText}
              />
            </S.TwoColumnBannerMediaContainer>
          </S.DesktopContainer>
        )}
      </S.TwoColumnBannerMediasContainer>
      <S.TwoColumnBannerDetails
        $aspectRatio={aspectRatio}
        $textWidth={textWidth}
        $textAlign={twoColumnTextAlignment[textAlignment ?? 'centre']}
      >
        {!!bannerTitle?.text && (
          <S.DesktopContainer $show={bannerTitle?.textSize === 'Headings - H5'}>
            <S.TwoColumnBannerTitle
              {...bannerTitle}
              text={getLocalizedValue(bannerTitle?.text)}
              defaultTextColor={colours.BLACK}
              defaultTextSize={TypographyStylesType.HEADINGS_X_LARGE}
              $textSize={bannerTitle?.textSize}
            />
          </S.DesktopContainer>
        )}
        {(bannerDescriptions as DoubleBannerDescriptions[] | undefined)?.map((description, index) => (
          <S.TwoColumnBannerDescription
            key={`${description.richText}-${index}`}
            text={getLocalizedValue(description.richText)}
            fontSize={TypographyStyles.Body.Medium.Regular}
            $textColor={description.textColor as string}
          />
        ))}
        {!!bannerButtons?.length && (
          <S.TwoColumnBannerButtonsContainer>
            {bannerButtons.slice(0, 4).map((button, idx) => (
              <li key={`${button.buttonText}-${idx}`}>
                <S.TwoColumnBannerButton $variation={button.buttonVariation} {...button.link} onClick={onClick}>
                  {getLocalizedValue(button.buttonText)}
                </S.TwoColumnBannerButton>
              </li>
            ))}
          </S.TwoColumnBannerButtonsContainer>
        )}
      </S.TwoColumnBannerDetails>
      {secondMedia && (
        <S.MobileContainer>
          <S.TwoColumnBannerMediaContainer $aspectRatio={aspectRatio} $mediaFit={secondMedia.mediaFit}>
            <MediaPartial
              {...(secondMedia as MediaPartialProps)}
              height={100}
              width={100}
              isImageLayoutFill
              mediaFit={secondMedia?.mediaFit}
              videoQuality={secondMedia?.videoQuality}
              altText={secondMedia?.altText}
            />
          </S.TwoColumnBannerMediaContainer>
        </S.MobileContainer>
      )}
    </S.TwoColumnBannerContainer>
  );
}

export default TwoColumnBanner;
